import { useEffect, useRef, useState } from 'react';
import { Mic, Play, Pause } from 'lucide-react';

/**
 * Cross-platform custom voice message player.
 *
 * Why this exists:
 *   iOS Safari refuses to read the duration from WebM blobs produced by
 *   MediaRecorder, and renders the native <audio controls> as "Error" instead
 *   of "0:00 / mm:ss". This player draws its own UI and uses the duration we
 *   captured at record-time (stored on the message as `duration`, in seconds).
 *
 * Props:
 *   - src: audio URL (S3 download URL)
 *   - duration: integer seconds — comes from message.duration. May be undefined
 *               for legacy messages recorded before duration tracking existed.
 *   - isOwn: boolean — affects color palette to match the message bubble
 *   - testId: optional data-testid prefix
 */
const formatTime = (secs) => {
  if (!Number.isFinite(secs) || secs < 0) return '0:00';
  const total = Math.floor(secs);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

export const VoiceMessagePlayer = ({ src, duration, isOwn = false, testId = 'voice-player' }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  // If we don't have a captured duration (legacy message), try to use the audio
  // element's own duration once it becomes available.
  const [fallbackDuration, setFallbackDuration] = useState(null);

  const effectiveDuration = Number.isFinite(duration) && duration > 0
    ? duration
    : fallbackDuration;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTimeUpdate = () => setCurrentTime(audio.currentTime || 0);
    const onEnded = () => { setIsPlaying(false); setCurrentTime(0); };
    const onPause = () => setIsPlaying(false);
    const onPlay = () => setIsPlaying(true);
    const onLoadedMeta = () => {
      // Trust audio.duration only if it's a finite, sensible number (Chrome/Firefox).
      // Safari will report Infinity or NaN for WebM blobs — ignore it then.
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        setFallbackDuration(audio.duration);
      }
    };
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('loadedmetadata', onLoadedMeta);
    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('loadedmetadata', onLoadedMeta);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) audio.play().catch(() => {});
    else audio.pause();
  };

  // Compute the progress percentage. If we don't have a known duration, fall
  // back to an indeterminate look (no progress fill).
  const progressPct = effectiveDuration ? Math.min(100, (currentTime / effectiveDuration) * 100) : 0;

  // Color palette — outgoing messages have a blue bubble, others a slate bubble.
  const trackColor = isOwn ? 'bg-white/25' : 'bg-slate-400/30';
  const fillColor = isOwn ? 'bg-white' : 'bg-[#2563eb]';
  const iconColor = isOwn ? 'text-white' : 'text-foreground';

  return (
    <div className="flex items-center gap-2 min-w-0" data-testid={testId}>
      <Mic className={`w-4 h-4 shrink-0 ${iconColor}`} />
      <button
        type="button"
        onClick={togglePlay}
        className={`flex items-center justify-center h-9 w-9 rounded-full shrink-0 ${isOwn ? 'bg-white/20 hover:bg-white/30' : 'bg-[#2563eb]/15 hover:bg-[#2563eb]/25'} transition-colors`}
        aria-label={isPlaying ? 'Pause voice message' : 'Play voice message'}
        data-testid={`${testId}-toggle`}
      >
        {isPlaying ? <Pause className={`w-4 h-4 ${iconColor}`} /> : <Play className={`w-4 h-4 ${iconColor}`} />}
      </button>

      <div className="flex-1 min-w-0">
        {/* Progress bar */}
        <div className={`h-1.5 rounded-full ${trackColor} overflow-hidden`}>
          <div className={`h-full ${fillColor} transition-[width] duration-100`} style={{ width: `${progressPct}%` }} />
        </div>
        {/* Time label */}
        <div className={`text-[11px] mt-1 tabular-nums ${isOwn ? 'text-white/80' : 'text-muted-foreground'}`} data-testid={`${testId}-time`}>
          {effectiveDuration
            ? `${formatTime(currentTime)} / ${formatTime(effectiveDuration)}`
            : (isPlaying ? formatTime(currentTime) : 'Voice message')}
        </div>
      </div>

      {/* Hidden HTML5 audio element — drives playback only, never shown */}
      <audio ref={audioRef} src={src} preload="metadata" playsInline className="hidden" />
    </div>
  );
};

export default VoiceMessagePlayer;
